import { useEffect, useRef } from "react";
import ReconnectingWebSocket from "reconnecting-websocket";
import { CashBoxDefinedSocketKey } from "../shared/types/socket";
import { cashBoxDefinedSocketKeys } from "../shared/lib/socket";

type PingSocketMessage = {
    type?: string;
    data?: unknown;
};

type AcknowledgeableSocketMessage = {
    message_id?: unknown;
};

type StableWebSocketOptions<MessageType = unknown> = {
    url?: string | null;
    onMessage?: (data: MessageType) => void;
    onOpen?: () => void | Promise<void>;
    onClose?: () => void;
    onError?: (error: unknown) => void;
    pingInterval?: number;
    pongTimeout?: number;
};

export default function useStableWebSocket<MessageType = unknown>({
    url,
    onMessage,
    onOpen,
    onClose,
    onError,
    pingInterval = 10000,
    pongTimeout = 30000,
}: StableWebSocketOptions<MessageType>) {
    const socketRef = useRef<ReconnectingWebSocket | null>(null);
    const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const lastPongRef = useRef(Date.now());
    const onMessageRef = useRef<StableWebSocketOptions<MessageType>["onMessage"]>(onMessage);
    const onOpenRef = useRef<StableWebSocketOptions<MessageType>["onOpen"]>(onOpen);
    const onCloseRef = useRef<StableWebSocketOptions<MessageType>["onClose"]>(onClose);
    const onErrorRef = useRef<StableWebSocketOptions<MessageType>["onError"]>(onError);

    useEffect(() => {
        onMessageRef.current = onMessage;
    }, [onMessage]);

    useEffect(() => {
        onOpenRef.current = onOpen;
    }, [onOpen]);

    useEffect(() => {
        onCloseRef.current = onClose;
    }, [onClose]);

    useEffect(() => {
        onErrorRef.current = onError;
    }, [onError]);

    useEffect(() => {
        if (!url) return;

        const socket = new ReconnectingWebSocket(url, [], {
            minReconnectionDelay: 5000,
            maxRetries: Infinity,
        });

        socketRef.current = socket;

        const clearPingInterval = () => {
            if (pingIntervalRef.current !== null) {
                clearInterval(pingIntervalRef.current);
            }
        };

        const handleOpen = () => {
            onOpenRef.current?.();

            // reset liveness timer when a new socket is opened
            lastPongRef.current = Date.now();

            clearPingInterval();

            sendPing();

            pingIntervalRef.current = setInterval(() => {
                const now = Date.now();

                // If we don't get a pong, the connection is suspect.
                if (now - lastPongRef.current > pongTimeout) {
                    console.warn("WS pong timeout → force reconnect");

                    try {
                        socket.reconnect(4000, "pong-timeout");
                    } catch {}
                    return;
                }

                sendPing();
            }, pingInterval);
        };

        const handleMessage = (event: MessageEvent<string>) => {
            let data: MessageType | PingSocketMessage;
            try {
                data = JSON.parse(event.data) as MessageType | PingSocketMessage;
            } catch {
                return;
            }

            // pong
            if (
                typeof data === "object" &&
                data !== null &&
                "type" in data &&
                "data" in data &&
                data.type === "ping" &&
                data.data === "pong"
            ) {
                lastPongRef.current = Date.now();
                return;
            }

            if (
                typeof data === "object" &&
                data !== null &&
                "type" in data &&
                cashBoxDefinedSocketKeys.includes(data.type as CashBoxDefinedSocketKey)
            ) {
                sendAck(data);
            }

            onMessageRef.current?.(data as MessageType);
        };

        const handleClose = () => {
            onCloseRef.current?.();
            clearPingInterval();
        };

        const handleError = (e: unknown) => {
            onErrorRef.current?.(e);
        };

        const sendPing = () => {
            if (socket.readyState === WebSocket.OPEN) {
                socket.send(JSON.stringify({ command: "ping" }));
            }
        };

        const sendAck = (data: MessageType | PingSocketMessage) => {
            if (
                typeof data !== "object" ||
                data === null ||
                !("message_id" in data)
            ) {
                return;
            }

            const { message_id } = data as AcknowledgeableSocketMessage;

            if (typeof message_id !== "string" || !message_id) {
                return;
            }

            if (socket.readyState === WebSocket.OPEN) {
                socket.send(JSON.stringify({ type: "ack", message_id }));
            }
        };

        socket.addEventListener("open", handleOpen);
        socket.addEventListener("message", handleMessage);
        socket.addEventListener("close", handleClose);
        socket.addEventListener("error", handleError);

        // network change support
        const handleOnline = () => {
            if (socket.readyState !== WebSocket.OPEN) {
                console.warn("Browser online → reconnect WS");
                socket.reconnect(4001, "online-reconnect");
            }
        };

        window.addEventListener("online", handleOnline);

        return () => {
            clearPingInterval();
            window.removeEventListener("online", handleOnline);

            socket.removeEventListener("open", handleOpen);
            socket.removeEventListener("message", handleMessage);
            socket.removeEventListener("close", handleClose);
            socket.removeEventListener("error", handleError);

            socket.close();
        };
    }, [url, pingInterval, pongTimeout]);
}
